/** Cursor-on-Target feed status. Config is env-side; this only reports. */

import { api, type ApiResult } from '@/api/client';

export interface CotStatus {
  configured: boolean;
  endpoint?: string;
  /**
   * Which TAK protocol version is going on the wire: `xml` (Version 0) or
   * `protobuf` (Version 1, framed for the transport).
   *
   * Worth showing next to the endpoint because the emitter does not negotiate.
   * A peer expecting the other version does not report an error — it reads the
   * bytes as a length and a garbage message, and the tracks simply never
   * appear. "Emitting" and "showing up on the tablet" are different claims,
   * and this is usually the setting that separates them.
   */
  format?: string;
  eventsSent?: number;
  lastSentAt?: number | null;
  lastError?: string | null;
  /** Present only when the endpoint is `tls:`. Null or absent otherwise. */
  tls?: CotTlsStatus | null;
}

/**
 * Mutual TLS to a TAK server. A server that refuses our client certificate
 * closes the socket without saying why, so the certificate's subject and
 * expiry are what tell an operator whether the fault is on this side.
 */
export interface CotTlsStatus {
  connected: boolean;
  clientSubject: string | null;
  /** Unix seconds. */
  clientNotAfter: number | null;
  serverSubject: string | null;
  protocol: string | null;
  /** False only when COT_TLS_SKIP_HOSTNAME is on; the chain is always checked. */
  verifyHostname: boolean;
  /** "system", or the path of the configured CA bundle. */
  trust: string;
  lastError: string | null;
}

export function getCotStatus(): Promise<ApiResult<CotStatus>> {
  return api.get<CotStatus>('/cot/status', { logged: false });
}
